import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuPlus } from 'react-icons/lu';

import { Action, type GUID, type InternalUserProfile } from '@ndvr/auth-api';

import { TrashIcon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import {
    type InternalUserTenantPermission,
    useAllTenantsQuery,
    useCreateTenantMutation,
    useGrantInternalUserTenantAccessMutation,
    useInternalUserTenantPermissionsQuery,
    useRevokeInternalUserTenantAccessMutation,
} from './internal-user-profile-queries';

type TenantPermissionsCardProps = {
    internalUserGUID?: GUID<InternalUserProfile>;
};

export const TenantPermissionsCard = ({ internalUserGUID }: TenantPermissionsCardProps) => {
    const grantTenantAccessMutation = useGrantInternalUserTenantAccessMutation();
    const revokeTenantAccessMutation = useRevokeInternalUserTenantAccessMutation();
    const createTenantMutation = useCreateTenantMutation();
    const { data: allTenants = [], isLoading: allTenantsLoading, isError: allTenantsError } = useAllTenantsQuery();

    const [selectedTenantGUID, setSelectedTenantGUID] = useState('');
    const [selectedTenantAction, setSelectedTenantAction] = useState<Action>(Action.WRITE);
    const [isAddPermissionOpen, setIsAddPermissionOpen] = useState(false);
    const [isCreateTenantOpen, setIsCreateTenantOpen] = useState(false);

    const {
        data: tenantPermissions = [],
        isLoading: tenantPermissionsLoading,
        isError: tenantPermissionsError,
        refetch: refetchTenantPermissions,
    } = useInternalUserTenantPermissionsQuery(internalUserGUID);

    const tenantPermissionRows = useMemo(() => {
        return tenantPermissions
            .filter((permission) => hasTenantAccess(permission))
            .sort((a, b) => a.tenant.name.localeCompare(b.tenant.name));
    }, [tenantPermissions]);

    const assignedTenantGUIDs = useMemo(() => {
        return new Set(
            tenantPermissions
                .filter((permission) => hasTenantAccess(permission))
                .map((permission) => permission.tenant.tenantGUID),
        );
    }, [tenantPermissions]);

    const availableTenantOptions = useMemo(() => {
        return allTenants.filter((tenant) => !assignedTenantGUIDs.has(tenant.tenantGUID));
    }, [allTenants, assignedTenantGUIDs]);

    const tenantPlaceholder = !internalUserGUID
        ? 'No internal user selected'
        : allTenantsLoading
          ? 'Loading tenants...'
          : allTenantsError
            ? 'Failed to load tenants'
            : availableTenantOptions.length === 0
              ? 'All tenants assigned'
              : 'Select tenant';

    const isTenantSelectDisabled =
        !internalUserGUID ||
        grantTenantAccessMutation.isPending ||
        allTenantsLoading ||
        allTenantsError ||
        availableTenantOptions.length === 0;

    const tenantActionOptions = useMemo(
        () => [
            { value: Action.READ, label: 'READ' },
            { value: Action.WRITE, label: 'WRITE' },
            { value: Action.FULL_CONTROL, label: 'FULL_CONTROL' },
        ],
        [],
    );

    const handleAddTenantPermission = () => {
        if (!internalUserGUID || !selectedTenantGUID) {
            return;
        }

        grantTenantAccessMutation.mutate(
            {
                tenantGUID: selectedTenantGUID,
                internalUserGUID,
                action: selectedTenantAction,
            },
            {
                onSuccess: () => {
                    setSelectedTenantGUID('');
                },
            },
        );
    };

    const handleToggleAddPermission = () => {
        setIsAddPermissionOpen((isOpen) => {
            if (isOpen) {
                setSelectedTenantGUID('');
                setSelectedTenantAction(Action.WRITE);
            }
            return !isOpen;
        });
    };

    const handleRemoveTenantPermission = (permission: InternalUserTenantPermission) => {
        if (!internalUserGUID) {
            return;
        }

        revokeTenantAccessMutation.mutate({
            tenantGUID: permission.tenant.tenantGUID,
            internalUserGUID,
        });
    };

    return (
        <>
            <Card>
                <CardHeader className="items-start gap-3 space-y-0 pt-5 pb-0 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle className="font-heading font-medium">Tenant Permissions</CardTitle>
                    {!isAddPermissionOpen && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleToggleAddPermission}
                            disabled={!internalUserGUID}
                        >
                            <LuPlus className="h-4 w-4" />
                            Add permission
                        </Button>
                    )}

                    {isAddPermissionOpen && (
                        <Button variant="outline" size="sm" onClick={() => setIsCreateTenantOpen(true)}>
                            <LuPlus className="h-4 w-4" />
                            New tenant
                        </Button>
                    )}
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                    {tenantPermissionsLoading ? (
                        <div className="text-muted-foreground text-sm">Loading tenant permissions...</div>
                    ) : tenantPermissionsError ? (
                        <div className="text-destructive flex flex-wrap items-center gap-3 text-sm">
                            <span>Failed to load tenant permissions.</span>
                            <Button variant="outline" size="sm" onClick={() => refetchTenantPermissions()}>
                                Retry
                            </Button>
                        </div>
                    ) : null}

                    {(tenantPermissionRows.length > 0 || isAddPermissionOpen) && (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[40%]">Tenant</TableHead>
                                    <TableHead className="w-[30%]">Permission</TableHead>
                                    <TableHead className="w-[30%] text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isAddPermissionOpen && (
                                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                                        <TableCell>
                                            <Select
                                                value={selectedTenantGUID}
                                                onValueChange={setSelectedTenantGUID}
                                                disabled={isTenantSelectDisabled}
                                            >
                                                <SelectTrigger aria-label="Tenant">
                                                    <SelectValue placeholder={tenantPlaceholder} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {availableTenantOptions.map((tenant) => (
                                                        <SelectItem key={tenant.tenantGUID} value={tenant.tenantGUID}>
                                                            {tenant.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                value={selectedTenantAction}
                                                onValueChange={(value) => setSelectedTenantAction(value as Action)}
                                                disabled={grantTenantAccessMutation.isPending}
                                            >
                                                <SelectTrigger aria-label="Permission">
                                                    <SelectValue placeholder="Select action" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {tenantActionOptions.map((action) => (
                                                        <SelectItem key={action.value} value={action.value}>
                                                            {action.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={handleAddTenantPermission}
                                                    disabled={
                                                        grantTenantAccessMutation.isPending ||
                                                        !selectedTenantGUID ||
                                                        !internalUserGUID
                                                    }
                                                >
                                                    {grantTenantAccessMutation.isPending ? 'Adding...' : 'Add'}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleToggleAddPermission}
                                                    disabled={grantTenantAccessMutation.isPending}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {tenantPermissionRows.map((permission) => (
                                    <TableRow key={permission.tenant.tenantGUID}>
                                        <TableCell>{permission.tenant.name}</TableCell>
                                        <TableCell>{permission.action}</TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                title="Remove permission"
                                                onClick={() => handleRemoveTenantPermission(permission)}
                                                disabled={revokeTenantAccessMutation.isPending}
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {tenantPermissionRows.length === 0 && !isAddPermissionOpen && (
                        <div className="text-muted-foreground text-sm">No tenant permissions assigned.</div>
                    )}
                </CardContent>
            </Card>

            <CreateTenantDialog
                open={isCreateTenantOpen}
                onOpenChange={setIsCreateTenantOpen}
                createTenantMutation={createTenantMutation}
            />
        </>
    );
};

const hasTenantAccess = (permission: InternalUserTenantPermission) =>
    permission.action === Action.READ ||
    permission.action === Action.WRITE ||
    permission.action === Action.FULL_CONTROL;

const CreateTenantDialog = ({
    open,
    onOpenChange,
    createTenantMutation,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    createTenantMutation: ReturnType<typeof useCreateTenantMutation>;
}) => {
    const createTenantForm = useForm<{ name: string }>({
        mode: 'onChange',
        defaultValues: { name: '' },
    });

    const handleCloseCreateTenant = () => {
        onOpenChange(false);
        createTenantForm.reset({ name: '' });
    };

    const handleCreateTenant = createTenantForm.handleSubmit((values) => {
        const trimmedName = values.name.trim();
        if (!trimmedName) {
            return;
        }

        createTenantMutation.mutate(
            { name: trimmedName },
            {
                onSuccess: () => {
                    handleCloseCreateTenant();
                },
            },
        );
    });

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCloseCreateTenant()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Create tenant</DialogTitle>
                    <DialogDescription>Add a new tenant for internal user access.</DialogDescription>
                </DialogHeader>
                <Form {...createTenantForm}>
                    <form onSubmit={handleCreateTenant} className="flex flex-col gap-4">
                        <FormField
                            control={createTenantForm.control}
                            name="name"
                            rules={{ required: 'Tenant name is required' }}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            placeholder="Tenant name"
                                            disabled={createTenantMutation.isPending}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button
                                variant="outline"
                                type="button"
                                onClick={handleCloseCreateTenant}
                                disabled={createTenantMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={createTenantMutation.isPending || !createTenantForm.watch('name')?.trim()}
                            >
                                {createTenantMutation.isPending ? 'Creating...' : 'Create tenant'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};
